import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import MerchantHeader from '../components/MerchantHeader';
import ErrorBoundary from '../components/ErrorBoundary';
import { LoadingPage, LoadingCard } from '../components/LoadingState';
import { toast } from 'sonner';
import {
  CreditCard, TrendingUp, TrendingDown, Zap, BarChart3,
  ArrowUpRight, AlertTriangle, Clock, DollarSign,
  Activity, Gauge, ChevronRight, Coins
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle,
} from '@/components/ui/card';
import {
  Tabs, TabsContent, TabsList, TabsTrigger,
} from '@/components/ui/tabs';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer, Cell,
  PieChart, Pie,
} from 'recharts';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function BillingDashboardPage() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [dashboard, setDashboard] = useState(null);
  const [dailyChart, setDailyChart] = useState(null);
  const [heatmap, setHeatmap] = useState(null);
  const [tierComparison, setTierComparison] = useState(null);
  const [roi, setRoi] = useState(null);

  const getToken = () => sessionStorage.getItem('session_token');

  const fetchWithAuth = useCallback(async (endpoint) => {
    const token = getToken();
    if (!token) { navigate('/login'); return null; }
    const res = await fetch(`${API_URL}${endpoint}`, {
      headers: { 'Authorization': `Bearer ${token}` },
    });
    if (!res.ok) throw new Error(`Failed to fetch ${endpoint}`);
    return res.json();
  }, [navigate]);

  const loadDashboard = useCallback(async () => {
    try {
      const [dash, chart, heat, tiers, roiData] = await Promise.all([
        fetchWithAuth('/api/v1/billing/dashboard'),
        fetchWithAuth('/api/v1/billing/chart/daily?days=30'),
        fetchWithAuth('/api/v1/billing/chart/heatmap?days=30'),
        fetchWithAuth('/api/v1/billing/compare-tiers'),
        fetchWithAuth('/api/v1/billing/roi?days=30'),
      ]);
      setDashboard(dash);
      setDailyChart(chart);
      setHeatmap(heat);
      setTierComparison(tiers);
      setRoi(roiData);
    } catch (err) {
      console.error('Dashboard load failed:', err);
      toast.error('Failed to load billing dashboard');
    } finally {
      setLoading(false);
    }
  }, [fetchWithAuth]);

  useEffect(() => { loadDashboard(); }, [loadDashboard]);

  if (loading) return (
    <div className="min-h-screen bg-zinc-950">
      <MerchantHeader />
      <div className="max-w-7xl mx-auto px-4 py-8">
        <LoadingCard count={4} />
      </div>
    </div>
  );

  const credits = dashboard?.credits || {};
  const projection = dashboard?.projection || {};
  const tier = dashboard?.tier || {};
  const outcomes = dashboard?.outcomes || {};

  const usagePct = credits.usage_pct || 0;
  const gaugeColor = usagePct > 90 ? 'text-red-400' : usagePct > 70 ? 'text-amber-400' : 'text-cyan-400';
  const progressColor = usagePct > 90 ? 'bg-red-500' : usagePct > 70 ? 'bg-amber-500' : 'bg-cyan-500';

  const tierColors = { free: 'bg-zinc-600', pro: 'bg-cyan-600', scale: 'bg-purple-600', enterprise: 'bg-amber-600' };

  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      <MerchantHeader />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-8">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-bold">Billing & Usage</h1>
            <p className="text-zinc-400 text-sm mt-1">Monitor credits, usage, and ROI</p>
          </div>
          <div className="flex gap-3">
            <Button variant="outline" size="sm" onClick={() => navigate('/dashboard/settings/billing/subscription')}>
              Manage Plan
            </Button>
            <Button size="sm" className="bg-cyan-600 hover:bg-cyan-700" onClick={() => navigate('/dashboard/settings/billing/subscription')}>
              <Zap className="w-4 h-4 mr-1" /> Upgrade
            </Button>
          </div>
        </div>

        {/* Credit Gauge Row */}
        <div className="grid gap-4 md:grid-cols-4 mb-8">
          <Card className="bg-zinc-900/70 border-zinc-800">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-1">
                <Gauge className="w-3.5 h-3.5" /> Credits Used
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{credits.used?.toLocaleString()}<span className="text-sm text-zinc-500 font-normal"> / {credits.total?.toLocaleString()}</span></div>
              <Progress value={usagePct} className={`mt-2 h-2 ${progressColor}`} />
              <p className={`text-xs mt-1 ${gaugeColor}`}>{usagePct}% used this cycle</p>
            </CardContent>
          </Card>

          <Card className="bg-zinc-900/70 border-zinc-800">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-1">
                <Activity className="w-3.5 h-3.5" /> Credits Remaining
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className={`text-2xl font-bold ${gaugeColor}`}>{credits.remaining?.toLocaleString()}</div>
              <p className="text-xs text-zinc-500 mt-2">
                {projection.will_exhaust_before_cycle_end
                  ? <span className="text-amber-400"><AlertTriangle className="w-3 h-3 inline mr-1" />Projected to run out in {projection.days_until_exhaustion} days</span>
                  : <span className="text-emerald-400">On track for the billing cycle</span>
                }
              </p>
            </CardContent>
          </Card>

          <Card className="bg-zinc-900/70 border-zinc-800">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-1">
                <DollarSign className="w-3.5 h-3.5" /> Commerce Value
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold text-emerald-400">${outcomes.total_commerce_value_usd?.toLocaleString()}</div>
              <p className="text-xs text-zinc-500 mt-2">
                ROI: <span className="text-cyan-400">{outcomes.roi_multiplier}x</span> return on spend
              </p>
            </CardContent>
          </Card>

          <Card className="bg-zinc-900/70 border-zinc-800">
            <CardHeader className="pb-2">
              <CardDescription className="flex items-center gap-1">
                <CreditCard className="w-3.5 h-3.5" /> Current Plan
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="flex items-center gap-2">
                <Badge className={`${tierColors[tier.current] || 'bg-zinc-600'} text-white capitalize`}>{tier.current}</Badge>
                <span className="text-xs text-zinc-500">{tier.rate_limit_per_sec} req/s</span>
              </div>
              <p className="text-xs text-zinc-500 mt-2">
                {tier.overage_allowed
                  ? <span className="text-emerald-400">Overage billing enabled</span>
                  : <span className="text-amber-400">Hard cap — upgrade for overage</span>
                }
              </p>
            </CardContent>
          </Card>
        </div>

        {/* Charts Section */}
        <Tabs defaultValue="usage" className="mb-8">
          <TabsList className="bg-zinc-900 border-zinc-800">
            <TabsTrigger value="usage">Daily Usage</TabsTrigger>
            <TabsTrigger value="heatmap">API Heatmap</TabsTrigger>
            <TabsTrigger value="roi">ROI</TabsTrigger>
          </TabsList>

          <TabsContent value="usage">
            <Card className="bg-zinc-900/70 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-base">Credit Usage — Last 30 Days</CardTitle>
                <CardDescription>Daily credits consumed and API calls</CardDescription>
              </CardHeader>
              <CardContent>
                <ErrorBoundary fallbackTitle="Chart Error">
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <AreaChart data={dailyChart?.data || []}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                        <XAxis dataKey="date" stroke="#71717a" tick={{ fontSize: 11 }}
                          tickFormatter={(val) => val ? val.slice(5) : ''} />
                        <YAxis stroke="#71717a" tick={{ fontSize: 11 }} />
                        <Tooltip
                          contentStyle={{ backgroundColor: '#18181b', border: '1px solid #3f3f46', borderRadius: 8 }}
                          labelStyle={{ color: '#a1a1aa' }}
                        />
                        <Area type="monotone" dataKey="credits_used" stroke="#06b6d4" fill="#06b6d4" fillOpacity={0.15} name="Credits" />
                        <Area type="monotone" dataKey="api_calls" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.1} name="API Calls" />
                      </AreaChart>
                    </ResponsiveContainer>
                  </div>
                </ErrorBoundary>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="heatmap">
            <Card className="bg-zinc-900/70 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-base">Endpoint Usage Heatmap</CardTitle>
                <CardDescription>Which APIs consume the most credits</CardDescription>
              </CardHeader>
              <CardContent>
                <ErrorBoundary fallbackTitle="Heatmap Error">
                  <div className="h-80">
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart data={(heatmap?.endpoints || []).slice(0, 10)} layout="vertical">
                        <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                        <XAxis type="number" stroke="#71717a" tick={{ fontSize: 11 }} />
                        <YAxis type="category" dataKey="endpoint" stroke="#71717a" tick={{ fontSize: 10 }} width={200}
                          tickFormatter={(val) => val?.length > 30 ? val.slice(0, 30) + '...' : val} />
                        <Tooltip contentStyle={{ backgroundColor: '#18181b', border: '1px solid #3f3f46', borderRadius: 8 }} />
                        <Bar dataKey="total_credits" fill="#06b6d4" radius={[0, 4, 4, 0]} name="Credits" />
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                </ErrorBoundary>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="roi">
            <Card className="bg-zinc-900/70 border-zinc-800">
              <CardHeader>
                <CardTitle className="text-base">Return on Investment</CardTitle>
                <CardDescription>Commerce value created vs total spend</CardDescription>
              </CardHeader>
              <CardContent>
                {roi && (
                  <div className="grid md:grid-cols-3 gap-6">
                    <div className="text-center p-6 rounded-lg bg-zinc-800/50">
                      <p className="text-xs text-zinc-400 mb-2">Commerce Value Created</p>
                      <p className="text-3xl font-bold text-emerald-400">${roi.commerce_value_created_usd?.toLocaleString()}</p>
                    </div>
                    <div className="text-center p-6 rounded-lg bg-zinc-800/50">
                      <p className="text-xs text-zinc-400 mb-2">Total Spend</p>
                      <p className="text-3xl font-bold text-zinc-300">${roi.total_spend?.total_usd?.toLocaleString()}</p>
                      <div className="text-xs text-zinc-500 mt-2 space-y-1">
                        <p>Credits: ${roi.total_spend?.credit_consumption_usd}</p>
                        <p>Overage: ${roi.total_spend?.overage_charges_usd}</p>
                        <p>Outcome fees: ${roi.total_spend?.outcome_fees_usd}</p>
                      </div>
                    </div>
                    <div className="text-center p-6 rounded-lg bg-zinc-800/50">
                      <p className="text-xs text-zinc-400 mb-2">ROI Multiplier</p>
                      <p className="text-3xl font-bold text-cyan-400">{roi.roi?.multiplier}x</p>
                      <p className="text-xs text-zinc-500 mt-2">
                        Net value: <span className="text-emerald-400">${roi.roi?.net_value_usd?.toLocaleString()}</span>
                      </p>
                      <p className="text-xs text-zinc-500">
                        Effective cost: {roi.roi?.effective_cost_pct}% of value
                      </p>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Top Endpoints Quick View */}
        <Card className="bg-zinc-900/70 border-zinc-800 mb-8">
          <CardHeader>
            <CardTitle className="text-base">Top API Endpoints This Month</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-3">
              {(dashboard?.top_endpoints || []).slice(0, 5).map((ep, i) => (
                <div key={i} className="flex items-center justify-between py-2 border-b border-zinc-800/50 last:border-0">
                  <div className="flex items-center gap-3">
                    <span className="text-xs text-zinc-500 w-5">{i + 1}.</span>
                    <code className="text-xs text-cyan-400 bg-zinc-800/50 px-2 py-1 rounded font-mono">{ep.endpoint}</code>
                  </div>
                  <div className="flex items-center gap-4">
                    <span className="text-xs text-zinc-400">{ep.calls?.toLocaleString()} calls</span>
                    <Badge variant="outline" className="text-xs">{ep.credits?.toLocaleString()} credits</Badge>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Projection Warning */}
        {projection.will_exhaust_before_cycle_end && (
          <Card className="bg-amber-950/30 border-amber-800/50 mb-8">
            <CardContent className="flex items-center gap-4 py-4">
              <AlertTriangle className="w-5 h-5 text-amber-400 flex-shrink-0" />
              <div className="flex-1">
                <p className="text-sm font-medium text-amber-300">
                  Credits projected to run out in {projection.days_until_exhaustion} days
                </p>
                <p className="text-xs text-amber-400/70 mt-0.5">
                  Avg daily usage: {Math.round(projection.avg_daily_credits)} credits/day. Consider upgrading or purchasing additional credits.
                </p>
              </div>
              <Button size="sm" className="bg-amber-600 hover:bg-amber-700 text-white" onClick={() => navigate('/dashboard/settings/billing/subscription')}>
                Upgrade Plan
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
